import React, { useState, useEffect } from "react";
import { FixedSizeGrid } from "react-window";
import {
  deleteImageGallery,
  DeleteMultipleImageGalleryApi,
  getMediaList,
} from "services/apiService";
import toast from "react-hot-toast";
import {
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Card,
  CardMedia,
  Skeleton,
  Box,
  Tooltip,
  Typography,
  CardContent,
  Checkbox,
  Backdrop,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import config from "config";

const MediaLibrary: React.FC = () => {
  const AppUrl = config?.REACT_APP_IMAGE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { data: mediaData, isLoading } = useQuery("mediaList", getMediaList);
  const [isMultipleDelete, setIsMultipleDelete] = useState(false);
  const [deleteMultipleImages, setdeleteMultipleImages] = useState({
    file_names: [],
  });

  const [filterType, setFilterType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [columnCount, setColumnCount] = useState<number>(5);
  const [gridWidth, setGridWidth] = useState(1500);

  const filteredMedia = React.useMemo(() => {
    return mediaData?.data?.filter((media: string) => {
      const matchesType = filterType === "all" || media.endsWith(filterType);
      const matchesQuery = media
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesType && matchesQuery;
    });
  }, [mediaData, filterType, searchQuery]);

  const handleDeleteImages = async (imgae) => {
    try {
      const response = await deleteImageGallery(imgae);
      if (response.status_code === 200) {
        toast.success("images successfully deleted");
        queryClient.invalidateQueries("mediaList");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCheckboxChange = (image) => {
    setdeleteMultipleImages((prevSelected) => ({
      file_names: prevSelected.file_names.includes(image)
        ? prevSelected.file_names.filter((name) => name !== image)
        : [...prevSelected.file_names, image],
    }));
  };

  const HandleDeleteMultipleImageGallery = async () => {
    if (deleteMultipleImages.file_names.length === 0) {
      return toast.error("please select the images & and click again !");
    }
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await DeleteMultipleImageGalleryApi(
            deleteMultipleImages
          );
          if (response.status_code === 200) {
            toast.success("Images successfully deleted");
            queryClient.invalidateQueries("mediaList");
            setdeleteMultipleImages({
              file_names: [],
            });
            setIsMultipleDelete(false);
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 2000) {
        setColumnCount(9);
        setGridWidth(1800);
      } else if (window.innerWidth >= 1800) {
        setColumnCount(7);
        setGridWidth(1450);
      } else if (window.innerWidth >= 1440) {
        setColumnCount(6);
        setGridWidth(1200);
      } else if (window.innerWidth >= 1024) {
        setColumnCount(5);
        setGridWidth(1000);
      } else {
        setColumnCount(4);
        setGridWidth(800);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isLoading) {
    return (
      <>
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Skeleton variant="rectangular" width={200} height={40} />
            <div>
              <Skeleton
                variant="rectangular"
                width={200}
                height={40}
                sx={{ marginRight: 2 }}
              />
              <Skeleton variant="rectangular" width={120} height={40} />
            </div>
          </div>
          <FixedSizeGrid
            columnCount={columnCount}
            rowCount={30}
            columnWidth={210}
            rowHeight={250}
            height={650}
            width={1500}
          >
            {({ columnIndex, rowIndex, style }) => (
              <div style={style} className="p-2">
                <Skeleton variant="rectangular" width={210} height={150} />
              </div>
            )}
          </FixedSizeGrid>
        </div>
      </>
    );
  }
  const Cell = ({
    columnIndex,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    rowIndex: number;
    style: React.CSSProperties;
  }) => {
    const index = rowIndex * columnCount + columnIndex;
    if (index >= filteredMedia?.length) return null;
    const media = filteredMedia[index];
    const isVideo = media.endsWith(".mp4");

    return (
      <div style={style} className="p-2">
        <Card
          sx={{
            position: "relative",
            overflow: "hidden",
            height: "100%",
            borderRadius: "5px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
            "&:hover .hover-overlay": {
              opacity: 1,
              transform: "translateY(0) !important",
            },
          }}
        >
          {isMultipleDelete && (
            <Checkbox
              checked={deleteMultipleImages?.file_names.includes(media)}
              onChange={() => handleCheckboxChange(media)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(255,255,255,0.8)",
                borderRadius: "50%",
                padding: "1px",
              }}
            />
          )}

          {isVideo ? (
            <video
              controls
              src={`${AppUrl}${media}`}
              style={{
                height: 170,
                width: "100%",
                objectFit: "contain",
                borderRadius: "10px 10px 0 0",
              }}
            />
          ) : (
            <>
              <CardMedia
                component="img"
                image={`${AppUrl}${media}`}
                alt="media"
                sx={{
                  height: 170,
                  objectFit: "contain",
                  backgroundColor: "#f5f5f5",
                  transition: "filter 0.3s ease",
                }}
              />

              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    fontWeight: 500,
                    padding: "8px",
                    color: "#333",
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {(() => {
                    const fileName = media.split("/").pop() || "";
                    return fileName.length > 18
                      ? `${fileName.slice(0, 18)}...`
                      : fileName;
                  })()}
                </Typography>
              </CardContent>
            </>
          )}

          <Box
            className="hover-overlay"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              background: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              padding: "12px",
              opacity: 0,
              transition: "opacity 0.4s ease, transform 0.4s ease !important",
              transform: "translateY(100%) !important",
              zIndex: 3,
              borderRadius: "0 0 10px 10px",
            }}
          >
            <Tooltip title="Delete" arrow>
              <FontAwesomeIcon
                icon={faTrash}
                style={{
                  color: "white",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "8px",
                  transition: "0.3s ease",
                }}
                onClick={() => handleDeleteImages(media)}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#ff4d4d")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "white")}
              />
            </Tooltip>
            <CopyToClipboard
              text={media}
              onCopy={() => toast.success("Image name copied!")}
            >
              <Tooltip title="Copy Image Name" arrow>
                <FontAwesomeIcon
                  icon={faClipboard}
                  style={{
                    color: "white",
                    fontSize: "20px",
                    cursor: "pointer",
                    padding: "8px",
                    transition: "0.3s ease",
                  }}
                />
              </Tooltip>
            </CopyToClipboard>
          </Box>
        </Card>
      </div>
    );
  };

  console.log(mediaData.data);

  return (
    <>
      <Backdrop
        open={loading}
        style={{ zIndex: 1300, color: "#fff", backdropFilter: "blur(5px)" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="d-flex justify-content-between align-items-center mb-3  mt-2">
        <FormControl variant="outlined" size="small" sx={{ width: "200px" }}>
          <InputLabel id="filter-label">Filter by Type</InputLabel>
          <Select
            labelId="filter-label"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="Filter by Type"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value=".jpg">JPG</MenuItem>
            <MenuItem value=".png">PNG</MenuItem>
            <MenuItem value=".gif">GIF</MenuItem>
            <MenuItem value=".mp4">MP4</MenuItem>
          </Select>
        </FormControl>
        <div className="d-flex gap-2">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ marginRight: 2 }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button
              variant="contained"
              onClick={() => navigate("/media-create")}
            >
              Add Media
            </Button>
            {!isMultipleDelete ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsMultipleDelete(true)}
              >
                Delete Images
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                onClick={HandleDeleteMultipleImageGallery}
              >
                {`Remove Images (${deleteMultipleImages.file_names.length})`}
              </Button>
            )}
            {isMultipleDelete && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      deleteMultipleImages.file_names.length ===
                        mediaData.data.length && mediaData.data.length > 0
                    }
                    onChange={(e) => {
                      setdeleteMultipleImages({
                        file_names: e.target.checked
                          ? mediaData.data.map((item) => item)
                          : [],
                      });
                    }}
                  />
                }
                label="Select All"
              />
            )}
          </div>
        </div>
      </div>
      <FixedSizeGrid
        columnCount={columnCount}
        rowCount={Math.ceil(filteredMedia?.length / columnCount)}
        columnWidth={200}
        rowHeight={250}
        height={650}
        width={gridWidth}
      >
        {Cell}
      </FixedSizeGrid>
    </>
  );
};
export default MediaLibrary;
